
import * as $ from 'jquery';

import * as EditorLocator from 'src/shared/tinymce/EditorLocator';

/**
 * Monitor that binds to TinyMCE and ensures that Ally attributes are not cleared by TinyMCE when elements are
 * updated. Currently supports:
 *
 *  * Using the "InstructureImage" plugin, ensures that the resulting image does not clear away any "data-ally-*"
 *    attributes
 *
 * @param  {String|Regex}   editorId    A strategy to pick which editor to bind to
 */
export function AllyAttributeMonitor(editorId) {
    this._editorId = editorId;
    this._reset();
}

/**
 * Reset the monitor to wait for a new editor whose id matches the editor id matcher.
 */
AllyAttributeMonitor.prototype._reset = function() {
    var self = this;

    self._editor = null;
    self._$imagePanelImage = null;

    // Wait for a new instance of the editor to become available
    EditorLocator.locate(self._editorId).then(function(editor) {
        self._bind(editor);
    });
};

/**
 * Bind listeners to the specified editor. The listener takes care of transfering custom Ally attributes when
 * content is updated. This binding also takes care of destroying everything when the editor is destroyed.
 *
 * @param  {Editor} editor  The TinyMCE editor to bind to
 */
AllyAttributeMonitor.prototype._bind = function(editor) {
    var self = this;

    // Bind listeners to the editor
    self._editor = editor;
    self._editor.on('ExecCommand', _onExecCommand);

    // Bind the listener for when the editor is removed (e.g., in the Canvas Syllabus tool, the editor is added and
    // removed dynamically, so this is needed to ensure we unbind and bind when it is used)
    window.tinymce.once('RemoveEditor', _onRemoveEditor);

    function _onExecCommand(e) {
        if (e.command === 'mceInstructureImage') {
            // When the "InstructureImage" panel is triggered, this command is invoked. The panel is invoked for
            // whatever the current selection is. We won't be able to get that selection when the panel is closed
            // anymore unfortunately, so we have to cache it here as an indication that the panel has been invoked
            // and what node it was invoked for
            self._$imagePanelImage = $(e.target.selection.getNode());
        } else if (e.command === 'mceInsertContent' && self._$imagePanelImage) {
            // When the "InstuctureImage" panel is saved, it triggers this command. If this was just a simple image
            // update, then both the `$selection` and `$insert` (i.e., new image content) should represent just one
            // single image tag
            var $selection = self._$imagePanelImage;
            var $insert = $(e.value);
            var $selectedImage = isSimpleImageUpdate($selection, $insert);

            // Indicate we are no longer looking at the image panel
            self._$imagePanelImage = null;

            // Ensure we have the expected state for a simple Canvas image update
            if ($selectedImage) {
                // Retain knowledge of if we actually added any custom attributes
                var attrChanged = false;

                // Carry over any custom `data-ally-*` attributes from the source selection to the target image
                $.each($selectedImage[0].attributes, function(i, attr) {
                    if (attr.name.indexOf('data-ally-') === 0 && !$insert.is(`[${ attr.name }]`)) {
                        attrChanged = true;
                        $insert.attr(attr.name, attr.value);
                    }
                });

                // Only perform additional manipulation if we are inserting new attributes
                if (attrChanged) {
                    // At this point, TinyMCE has already inserted the image. The caret is sitting right before the
                    // image (i.e., `e.target.selection.getStart()` refers to the image it just inserted, which is
                    // the one with `data-ally-*` attributes stripped). We want to remove that one and instead
                    // insert the one that has the `data-ally` attributes
                    e.target.dom.remove(e.target.selection.getStart());
                    e.target.selection.setNode($insert[0]);
                }
            }
        } else {
            // If this is some unknown command execution, we are probably not in the image panel
            self._$imagePanelImage = null;
        }
    }

    function _onRemoveEditor(e) {
        if (e.editor.id === self._editorId) {
            // Unbind our listener from the editor and wait for another one to become available
            self._editor.off('ExecCommand', _onExecCommand);
            self._reset();
        } else {
            // Some other editor was removed. Wait for another RemoveEditor event
            window.tinymce.once('RemoveEditor', _onRemoveEditor);
        }
    }
};

/**
 * Determine if the update represents a simple single image update in Canvas. For this to be true, the initial
 * selection and the inserted content must both be just a single image. Also, the `src` attribute of the image
 * should not change in the updated content. If it does, we bail on trying to carry forward attributes.
 *
 * @param  {jQuery} $selection  The selection jQuery object being updated
 * @param  {jQuery} $insert     The content jQuery being inserted
 * @return {jQuery}             A jQuery object of the image in the selection being updated. If this return value is
 *                              falsey, then it indicates this is not a compatible update
 */
function isSimpleImageUpdate($selection, $insert) {
    var $simpleImageSelection = null;
    if ($selection.length === 1 && $selection.is('img')) {
        $simpleImageSelection = $selection;
    } else if ($selection.length === 1 && $selection.find('img').length === 1) {
        $simpleImageSelection = $selection.find('img');
    }
    var simpleImageSelectionSrc = $simpleImageSelection && $simpleImageSelection.attr('src');

    var isSimpleImageInsert = ($insert.length === 1) && ($insert.is('img')) &&
        (simpleImageSelectionSrc === $insert.attr('src'));
    return isSimpleImageInsert && $simpleImageSelection;
}
